/**
 * Raw WebSocket Adapter for API Gateway WebSocket
 *
 * Provides a Socket.IO-like interface for raw WebSocket connections.
 * Handles reconnection, visibility-based recovery, and keepalive pings.
 *
 * Console statements here are intentional connection-lifecycle diagnostics
 * (connect / disconnect / reconnect / parse failures) for debugging WebSocket
 * issues in the field, so no-console is disabled for this low-level adapter.
 */
/* eslint-disable no-console */

import Cookies from 'js-cookie';
import { isGuestSession } from 'utils/auth';
import {
  extractAccessTokenFromDescopeCookie,
  extractRefreshTokenFromDescopeCookie
} from 'utils/functions';
import { isLatencyProbeEnabled, recordWsRtt, subscribeLatencyProbe } from 'utils/perfCounters';
import { DESCOPE_COOKIE } from 'utils/urls';

import {
  freshLiveness,
  isLivenessStale,
  onInboundMessage as onLivenessMessage,
  onKeepalivePing,
  onOpen as onLivenessOpen,
  PingLiveness
} from './socketLiveness';

export type EventHandler = (data?: any) => void;
export type Callback = (error?: any, data?: any) => void;

export interface SocketIOLikeInterface {
  emit: (event: string, data?: any, callback?: Callback) => void;
  on: (event: string, handler: EventHandler) => void;
  off: (event: string, handler?: EventHandler) => void;
  once: (event: string, handler: EventHandler) => void;
  removeAllListeners: (event?: string) => void;
  removeListener: (event: string, handler?: EventHandler) => void;
  listeners: (event: string) => EventHandler[];
  hasListeners: (event: string) => boolean;
  compress: (value: boolean) => SocketIOLikeInterface;
  /**
   * Register a private tap on every dispatched event, outside the handler set a
   * consumer's `off(event)` can clear. Optional: only the raw adapter has one.
   */
  addTap?: (fn: (event: string, data: any) => void) => () => void;
  connect: () => void;
  disconnect: () => void;
  connected: boolean;
  id?: string;
  auth?: any;
  io?: any;
  volatile?: any;
  timeout?: (ms: number) => any;
}

const RECONNECT_DELAY_BASE = 2000;
const RECONNECT_DELAY_MAX = 30000;
const RECONNECT_MAX_ATTEMPTS = 50;
// Keepalive AND liveness probe. Was 5 min (only to beat API Gateway's 10 min
// idle timeout); shortened so a silent half-open socket is DETECTED, not just
// kept warm. Each tick doubles as a liveness check (see keepaliveTick): after
// MAX_MISSED_PONGS unanswered pings the socket is treated as dead and force-
// reconnected. 25s × 2 misses ≈ 50–75s to recover a silently-dropped socket
// (vs. never). Still far under the 10 min idle timeout.
const PING_INTERVAL = 25 * 1000;
// When the perf HUD is watching, probe RTT this often. The probe timer always
// ticks but only sends a ping while isLatencyProbeEnabled() — so production
// (HUD off) keeps the cheap 5-min keepalive and never adds ping-route load.
const RTT_PROBE_INTERVAL = 5000;

export class RawWebSocketAdapter implements SocketIOLikeInterface {
  private ws: WebSocket | null = null;
  private url: string;
  private authToken: { access_token: string | null; refresh_token: string | null };
  private eventHandlers: Map<string, Set<EventHandler>> = new Map();

  /**
   * Private taps on every dispatched event, run before the public handler Set.
   *
   * Deliberately NOT part of `eventHandlers`, and deliberately a Set rather than
   * a single property. Consumers call `off(event)` with no handler - `App.tsx`'s
   * effect cleanup does exactly that for `gotTexas`/`gotOmaha` - and `off`
   * without a handler deletes the whole Set for that event, which silently took
   * the ConnectionManager's own listeners with it (root cause 9: the feed
   * watchdog went deaf and fired spurious repairs).
   *
   * A single assignable property fixed the `off()` half and left the same
   * failure reachable through `=`: any second writer (a debug tap, the Sentry
   * frame logger in this RCA's own action items) would silently replace the
   * manager's tap. Raised in PR review; a Set with add/remove has no
   * single-writer invariant to violate.
   */
  private taps = new Set<(event: string, data: any) => void>();

  /** Register a tap. Returns its remover. Idempotent for the same function. */
  addTap(fn: (event: string, data: any) => void): () => void {
    this.taps.add(fn);
    return () => {
      this.taps.delete(fn);
    };
  }

  /** Number of registered taps. Diagnostic, and asserted in tests. */
  get tapCount(): number {
    return this.taps.size;
  }
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private messageQueue: Array<{ event: string; data?: any; callback?: Callback }> = [];
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private rttProbeTimer: ReturnType<typeof setInterval> | null = null;
  // Unsubscribe handle for HUD probe on/off notifications. null = not subscribed.
  private unsubscribeProbe: (() => void) | null = null;
  // Send time of the most recent ping awaiting a pong. 0 = none outstanding.
  private pingSentAt = 0;
  // Keepalive pong-liveness accounting (half-open-socket detection). Pure logic
  // in socketLiveness.ts so the threshold is unit-testable.
  private liveness: PingLiveness = freshLiveness();
  private intentionalDisconnect = false;

  public connected = false;
  public id?: string;

  constructor(
    url: string,
    authToken: { access_token: string | null; refresh_token: string | null }
  ) {
    this.url = url;
    this.authToken = authToken;
    this.connect();
    // Lifecycle listeners (visibilitychange, focus, appStateChange) are handled
    // by ConnectionManager — this adapter only handles WebSocket-level reconnection.
  }

  /**
   * Re-read the Descope cookie to get a fresh token before connecting.
   * This handles token expiry during long sessions.
   */
  private refreshAuthToken() {
    // Cookie-only re-read (no localStorage fallback) — on native/iOS the
    // cookie can legitimately be absent while a valid token still lives in
    // localStorage (see hasValidAuthToken()'s fallback in utils/auth). Leave
    // this.authToken untouched when there's no cookie rather than clearing
    // it, so a constructor-supplied localStorage-sourced token isn't wiped
    // out by every subsequent connect() attempt. connect() derives the
    // guest/member handshake from isGuestSession() (below), not from
    // whether this.authToken happens to be stale, so a leftover value here
    // is harmless either way.
    const descopeCookie = Cookies.get(DESCOPE_COOKIE as string);
    if (descopeCookie && typeof descopeCookie === 'string') {
      this.authToken = {
        access_token: extractAccessTokenFromDescopeCookie(descopeCookie),
        refresh_token: extractRefreshTokenFromDescopeCookie(descopeCookie)
      };
    }
  }

  connect() {
    // Guard against double connections
    if (this.ws) {
      if (this.ws.readyState === WebSocket.OPEN) return;
      if (this.ws.readyState === WebSocket.CONNECTING) return;
      // CLOSING or CLOSED — clean up
      this.ws = null;
    }

    this.intentionalDisconnect = false;

    // Subscribe (once) to HUD probe toggles so the RTT-probe timer only exists
    // while the HUD is open. Idempotent across reconnects on the same instance.
    if (!this.unsubscribeProbe) {
      this.unsubscribeProbe = subscribeLatencyProbe((enabled) => {
        if (enabled) this.startRttProbe();
        else this.stopRttProbe();
      });
    }

    // Refresh token before each connection attempt
    this.refreshAuthToken();

    const separator = this.url.includes('?') ? '&' : '?';
    // Guests (no token) connect as observers: the API Gateway $connect handler
    // accepts `guest=true` and stores a receive-only guest connection. Guest
    // status is derived from isGuestSession() (which checks token EXPIRY, not
    // just presence) rather than merely `!token` — a stale/expired token left
    // in this.authToken from before the session reverted to guest must never
    // be sent as if it were live, or the $connect handler rejects it and the
    // update socket never establishes for what is now a guest session.
    const token = this.authToken.access_token;
    const wsUrl = isGuestSession()
      ? `${this.url}${separator}guest=true`
      : `${this.url}${separator}token=${encodeURIComponent(token || '')}`;

    console.log('[RawWebSocket] Connecting to:', this.url);

    try {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('[RawWebSocket] Connected');
        this.connected = true;
        this.reconnectAttempts = 0;
        this.id = Math.random().toString(36).substring(2, 15);
        // Clear any ping left outstanding from a previous connection so a late
        // pong can't be matched against it and report a bogus round-trip, and
        // start liveness accounting from a clean slate.
        this.pingSentAt = 0;
        this.liveness = onLivenessOpen();

        this.triggerEvent('connect', {});

        // Flush queued messages
        if (this.messageQueue.length > 0) {
          console.log(`[RawWebSocket] Flushing ${this.messageQueue.length} queued messages`);
          const queuedMessages = [...this.messageQueue];
          this.messageQueue = [];
          queuedMessages.forEach(({ event, data, callback }) => {
            this.emit(event, data, callback);
          });
        }

        // Start keepalive ping
        this.startPing();
      };

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);

          // ANY inbound frame proves the socket is alive → clear the half-open
          // miss counter. This is deliberately NOT limited to pongs: the server's
          // `ping` route only emits `pong` on its success path (it does a DDB
          // lookup + RDS UPDATE first), so a healthy socket that's still streaming
          // game/tournament broadcasts could miss pongs under RDS contention and
          // get torn down — the exact failure the Unity client hit relying on
          // ping/pong for liveness. Broadcasts keep an active table alive; the
          // ping only exists to generate traffic so we can spot a *silent* socket.
          this.liveness = onLivenessMessage();

          // True round-trip time: the server's `ping` route replies with
          // {event:'pong'}. Match it to our last outstanding ping and record
          // the delta. Still falls through to normal dispatch below.
          if (message.event === 'pong' || message.action === 'pong') {
            if (this.pingSentAt > 0) {
              recordWsRtt(Date.now() - this.pingSentAt);
              this.pingSentAt = 0;
            }
          }

          if (message.event) {
            const { event: evt, ...data } = message;
            // API Gateway frames use `{ event, data }`, mirroring Socket.IO's
            // `on(event, data)` contract. Unwrap the payload centrally so all
            // existing listeners receive the same value on either transport.
            this.triggerEvent(
              evt,
              Object.prototype.hasOwnProperty.call(message, 'data') ? message.data : data
            );
          } else if (message.action) {
            const { action, ...data } = message;
            this.triggerEvent(action, data);
          } else if (message.type) {
            const { type, ...data } = message;
            this.triggerEvent(type, data);
          } else {
            this.triggerEvent('message', message);
          }
        } catch (error) {
          console.error('[RawWebSocket] Error parsing message:', error);
          this.triggerEvent('error', { message: 'Failed to parse message', error });
        }
      };

      this.ws.onerror = (error) => {
        console.error('[RawWebSocket] Error:', error);
        this.triggerEvent('connect_error', error);
      };

      this.ws.onclose = (event) => {
        console.log('[RawWebSocket] Disconnected:', event.code, event.reason);
        this.connected = false;
        this.ws = null;
        this.stopPing();

        this.triggerEvent('disconnect', event.reason || 'transport close');

        // Only reconnect if not intentional
        if (!this.intentionalDisconnect) {
          this.scheduleReconnect();
        }
      };
    } catch (error) {
      console.error('[RawWebSocket] Failed to create WebSocket:', error);
      this.triggerEvent('connect_error', error);
      if (!this.intentionalDisconnect) {
        this.scheduleReconnect();
      }
    }
  }

  /** Send one ping, stamping the send time so the pong can be timed. */
  private sendPing() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.pingSentAt = Date.now();
        this.ws.send(JSON.stringify({ action: 'ping' }));
      } catch {
        /* noop */
      }
    }
  }

  private startPing() {
    this.stopPing();
    // Keepalive: keeps the API Gateway connection from idling out AND doubles as
    // the half-open-socket liveness check (keepaliveTick).
    this.pingTimer = setInterval(() => this.keepaliveTick(), PING_INTERVAL);
    // If the HUD is already watching when we (re)connect, begin probing now.
    if (isLatencyProbeEnabled()) this.startRttProbe();
  }

  /**
   * One keepalive interval: if NO inbound frame arrived since the last tick
   * (onmessage clears the flag on any message — broadcast or pong), it counts as
   * a miss; after MAX_MISSED_PONGS consecutive silent ticks the socket is
   * dead-but-open and we force a reconnect. Otherwise send the next ping. A miss
   * therefore means "total silence," not merely "the ping lambda didn't answer"
   * — so an actively-broadcasting socket is never torn down (EN-9731).
   */
  private keepaliveTick() {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const { next, dead } = onKeepalivePing(this.liveness);
    this.liveness = next;
    if (dead) {
      this.handleLivenessDrop();
      return;
    }
    this.sendPing();
  }

  /**
   * The socket reports OPEN but keepalive pongs have stopped — a half-open
   * connection the browser never fired `onclose` for. Tear it down as if it had
   * closed (detaching handlers so the eventual real onclose can't double-fire)
   * and reconnect via the normal backoff path.
   */
  private handleLivenessDrop() {
    console.warn('[RawWebSocket] Keepalive pongs missed — socket is half-open, forcing reconnect');
    this.connected = false;
    this.stopPing();
    this.pingSentAt = 0;
    this.liveness = freshLiveness();

    const stale = this.ws;
    this.ws = null;
    if (stale) {
      stale.onopen = null;
      stale.onmessage = null;
      stale.onerror = null;
      stale.onclose = null;
      try {
        stale.close(4000, 'liveness-timeout');
      } catch {
        /* noop */
      }
    }

    this.triggerEvent('disconnect', 'liveness timeout');
    if (!this.intentionalDisconnect) {
      this.scheduleReconnect();
    }
  }

  /**
   * True when the socket still reports connected but a keepalive pong has
   * already been missed — i.e. it looks half-open. The visibility/focus
   * recovery path uses this to force an immediate reconnect when the player
   * returns, instead of waiting for keepaliveTick to reach the dead threshold.
   */
  isStale(): boolean {
    return this.connected && isLivenessStale(this.liveness);
  }

  private stopPing() {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
    this.stopRttProbe();
  }

  /**
   * Begin frequent RTT probing — only ever runs while the HUD has the probe on.
   * No-op if already probing or not currently connected (onopen restarts it on
   * reconnect). Fires one ping immediately so the HUD gets a reading fast.
   */
  private startRttProbe() {
    if (this.rttProbeTimer || !this.connected) return;
    this.sendPing();
    this.rttProbeTimer = setInterval(() => this.sendPing(), RTT_PROBE_INTERVAL);
  }

  private stopRttProbe() {
    if (this.rttProbeTimer) {
      clearInterval(this.rttProbeTimer);
      this.rttProbeTimer = null;
    }
  }

  private scheduleReconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    if (this.reconnectAttempts >= RECONNECT_MAX_ATTEMPTS) {
      console.error(
        `[RawWebSocket] Max reconnect attempts (${RECONNECT_MAX_ATTEMPTS}) reached. Giving up.`
      );
      this.triggerEvent('reconnect_failed', { attempts: this.reconnectAttempts });
      return;
    }

    const delay = Math.min(
      RECONNECT_DELAY_BASE * Math.pow(1.5, this.reconnectAttempts),
      RECONNECT_DELAY_MAX
    );

    console.log(
      `[RawWebSocket] Reconnecting in ${Math.round(delay)}ms (attempt ${
        this.reconnectAttempts + 1
      }/${RECONNECT_MAX_ATTEMPTS})`
    );

    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++;
      // Refresh token before reconnecting — the original token may have expired
      try {
        // Dynamic import to avoid circular dependency
        import('utils/auth')
          .then(({ getAuthToken }) => {
            const freshToken = getAuthToken();
            if (freshToken.access_token) {
              this.authToken = freshToken;
            }
            this.connect();
          })
          .catch(() => {
            // Fallback: refreshAuthToken reads the cookie directly
            this.refreshAuthToken();
            this.connect();
          });
      } catch {
        this.refreshAuthToken();
        this.connect();
      }
    }, delay);
  }

  /** Force an immediate reconnect (e.g. on visibility change) */
  private reconnectNow() {
    console.log(
      '[RawWebSocket] reconnectNow called, connected:',
      this.connected,
      'intentional:',
      this.intentionalDisconnect
    );
    if (this.intentionalDisconnect) return;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (!this.connected) {
      this.connect();
    }
  }

  disconnect() {
    this.intentionalDisconnect = true;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // Drop the probe subscription so a replaced/torn-down adapter can't be
    // revived by a later HUD toggle (and can be garbage-collected).
    if (this.unsubscribeProbe) {
      this.unsubscribeProbe();
      this.unsubscribeProbe = null;
    }

    this.stopPing();

    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)
    ) {
      this.ws.close(1000, 'Client disconnect');
    }

    this.ws = null;
    this.connected = false;
  }

  /** Clean up all listeners and timers (call on unmount) */
  destroy() {
    this.disconnect();
    this.eventHandlers.clear();
    this.messageQueue = [];
  }

  emit(event: string, data?: any, callback?: Callback) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('[RawWebSocket] Not connected, queueing message:', event);
      this.messageQueue.push({ event, data, callback });
      return;
    }

    const message = {
      action: event,
      ...data
    };

    try {
      this.ws.send(JSON.stringify(message));

      if (callback) {
        setTimeout(() => callback(null), 0);
      }
    } catch (error) {
      console.error('[RawWebSocket] Failed to send:', error);
      if (callback) callback(error);
    }
  }

  on(event: string, handler: EventHandler) {
    let handlers = this.eventHandlers.get(event);
    if (!handlers) {
      handlers = new Set();
      this.eventHandlers.set(event, handlers);
    }
    handlers.add(handler);
  }

  off(event: string, handler?: EventHandler) {
    if (handler) {
      const handlers = this.eventHandlers.get(event);
      if (handlers) {
        handlers.delete(handler);
      }
    } else {
      this.eventHandlers.delete(event);
    }
  }

  once(event: string, handler: EventHandler) {
    const onceWrapper = (data?: any) => {
      this.off(event, onceWrapper);
      handler(data);
    };
    this.on(event, onceWrapper);
  }

  removeAllListeners(event?: string) {
    if (event) {
      this.eventHandlers.delete(event);
    } else {
      this.eventHandlers.clear();
    }
  }

  removeListener(event: string, handler?: EventHandler) {
    this.off(event, handler);
  }

  listeners(event: string): EventHandler[] {
    const handlers = this.eventHandlers.get(event);
    return handlers ? Array.from(handlers) : [];
  }

  hasListeners(event: string): boolean {
    const handlers = this.eventHandlers.get(event);
    return handlers ? handlers.size > 0 : false;
  }

  compress(_value: boolean): SocketIOLikeInterface {
    return this;
  }

  private triggerEvent(event: string, data: any) {
    // Owner taps run first, and deliberately do NOT live in `eventHandlers` -
    // see the `taps` field note. One throwing tap must not stop the others or
    // the public handlers.
    this.taps.forEach((tap) => {
      try {
        tap(event, data);
      } catch (error) {
        console.error(`[RawWebSocket] Error in owner tap for ${event}:`, error);
      }
    });
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(data);
        } catch (error) {
          console.error(`[RawWebSocket] Error in event handler for ${event}:`, error);
        }
      });
    }
  }

  get auth() {
    return this.authToken;
  }

  set auth(value: any) {
    this.authToken = value;
    if (this.connected) {
      this.disconnect();
      this.intentionalDisconnect = false;
      this.connect();
    }
  }

  get io() {
    return {
      on: (event: string, handler: EventHandler) => {
        if (event === 'close') {
          this.on('disconnect', handler);
        }
      },
      open: (callback: Callback) => {
        if (this.connected) {
          callback(null);
        } else {
          callback(new Error('Not connected'));
        }
      }
    };
  }

  get volatile() {
    return this;
  }

  timeout(_ms: number) {
    return this;
  }
}

export function createSocketAdapter(
  url: string,
  authToken: { access_token: string | null; refresh_token: string | null },
  useRawWebSocket = false
): SocketIOLikeInterface {
  if (useRawWebSocket || url.includes('ws.')) {
    return new RawWebSocketAdapter(url, authToken);
  } else {
    throw new Error('Socket.IO should be created directly in SocketContext');
  }
}
